/* eslint-disable */

import {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { androidEvents, androidParams } from "./android/arrays";
import {
  Box,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Container,
  Chip,
  IconButton,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import {
  DJCodeBlock,
  DJSimpleCodeBlock,
  ICodeBlock,
} from "../main/components/codeblock";
import { IProgress, ISessionProps, OSType } from "../main/components/IProgress";
import { useLocation, useNavigate } from "react-router-dom";
import { IOption } from "./SendInappEventParam";
import {
  Typography,
  TextField,
  Select,
  ChipSelection,
  MultiSelectMenu,
} from "@appsflyer/fe-ui-core";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import {
  amplitudeTrack_continue,
  amplitudeTrack_inputChange,
  InputType,
} from "../services/utils";
import AmplitudeLink from "../services/AmplitudeLink";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import CloseIcon from "@mui/icons-material/Close";
import { EvgenEvent, EvgenEventsObject } from "./IEvgen";
import { iosEvents, iosParameter } from "./ios/arrays";
import ExplanationTooltip from "../main/components/ExplanationTooltip";
import transition from "../main/components/transition";
import DragAndDropUpload from "../main/components/DragDropUpload";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { getCodeSnippet, interpolateTemplate } from "./codeTemplateUtils";

interface IAdRevenueParam {
  type: "predefined" | "custom";
  value: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const EventsTable = (props: EvgenEventsObject) => {
  const { t } = useTranslation();

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("ip_AppEvents_Evgen_ename")}</TableCell>
            <TableCell>{t("ip_AppEvents_Evgen_epurpose")}</TableCell>
            <TableCell>{t("ip_AppEvents_Evgen_etrigger")}</TableCell>
            <TableCell>{t("ip_AppEvents_Evgen_eparams")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props?.events.map((row, indx) => (
            <TableRow key={row.eventIdentifier + "_" + indx} hover>
              <TableCell>
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  flexDirection={"column"}
                  gap={1}
                >
                  <div>
                    <Chip
                      label={row.eventType ?? ""}
                      size="medium"
                      variant="outlined"
                      color={
                        row.eventType == "predefined" ? "primary" : "success"
                      }
                    />
                  </div>
                  <code>{row.eventIdentifier}</code>
                </Box>
              </TableCell>
              <TableCell>{row.eventPurposeDescription}</TableCell>
              <TableCell>{row.eventTriggerDescription}</TableCell>
              <TableCell>
                <Box display={"flex"} flexDirection={"column"} gap={1}>
                  {row.parameters.map((param, idx) => {
                    return (
                      <span key={param.parameterIdentifier + "_" + idx}>
                        <code>{param.parameterIdentifier}</code>
                      </span>
                    );
                  })}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface MultiSelectControlProps {
  selectedValue?: IOption;
  options: IOption[];
  onSelectedEventsChange?: (selectedValues: IOption[]) => void;
}

const MultiSelectControl = (props: MultiSelectControlProps) => {
  const [selectedEventsValues, setSelectedEventsValues] = useState<IOption[]>(
    []
  );
  const { t } = useTranslation();

  useEffect(() => {
    props.onSelectedEventsChange?.(selectedEventsValues);
  }, [selectedEventsValues]);

  const shouldRenderDeleteIcon = selectedEventsValues.length > 0;
  return (
    <ChipSelection
      label={t("ipAppEvents_Evgen_filter") + ": "}
      chipDefaultValue="All"
      controlType="multiSelect"
      value={selectedEventsValues}
      onChange={setSelectedEventsValues}
      deleteIcon={
        shouldRenderDeleteIcon ? <CloseIcon fontSize="small" /> : undefined
      }
      onDelete={
        shouldRenderDeleteIcon ? () => setSelectedEventsValues([]) : undefined
      }
      renderComponent={({ value, onChange, isOpen, setIsOpen, anchorRef }) => (
        <MultiSelectMenu
          options={props.options}
          values={value}
          {...{
            onChange,
            isOpen,
            setIsOpen,
            anchorRef,
          }}
        />
      )}
    />
  );
};

const SendInapp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<IProgress[]>([]);
  const { t } = useTranslation();
  const isReactNativeAndroid = location.state.os === OSType.ReactNativeAndroid;
  const isReactNativeIOS = location.state.os === OSType.ReactNativeIOS;
  const isReactNative = isReactNativeAndroid || isReactNativeIOS;
  const isAndroid = location.state.os === OSType.Android;
  const isIOS = location.state.os === OSType.iOS;
  const isAndroidLike = isAndroid || isReactNativeAndroid;

  // Radio group for the first section
  const [eventName, setEventName] = useState<{
    type: "predefined" | "custom";
    value: string;
  }>({
    type: "predefined",
    value: "",
  });

  const handleEventNameTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEventName({
      type: e.target.value as "predefined" | "custom",
      value: "",
    });
  };

  const handleEventNameValueChange = (value: string | IOption) => {
    setEventName({
      ...eventName,
      value: typeof value === "object" ? value.value : value,
    });
  };
  const [eventParams, setEventParams] = useState<IAdRevenueParam[]>([
    { type: "predefined", value: "" },
  ]);
  const [responseListenerRequired, setResponseListenerRequired] =
    useState(false);
  const paramValues = eventParams.map((p) => p.value).filter(Boolean);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const maxParamCount = 5;

  const handleAddParameter = () => {
    if (eventParams.length < maxParamCount) {
      const newParam: IAdRevenueParam = {
        type: "predefined",
        value: "",
      };
      setEventParams([...eventParams, newParam]);
    }
  };

  const handleRemoveParameter = (index: number) => {
    const updated = [...eventParams];
    updated.splice(index, 1);
    setEventParams(updated);
  };
  const handleParamTypeChange = (
    index: number,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const updated = [...eventParams];
    updated[index].type = event.target.value as "predefined" | "custom";
    if (event.target.value === "predefined") updated[index].value = "";
    setEventParams(updated);
  };

  const handleParamValueChange = (index: number, value: string) => {
    const updated = [...eventParams];
    updated[index].value = value;
    setEventParams(updated);
  };

  const handleValueChange = (index: number, value: string | IOption) => {
    const updated = [...eventParams];

    // Fix: unwrap Select value if it's an object
    updated[index].value = typeof value === "object" ? value.value : value;

    setEventParams(updated);
  };

  const [generatedEventCodePrimary, setGeneratedEventCodePrimary] =
    useState<string>(`Map<String, Object> eventValues = new HashMap<String, Object>();
eventValues.put(AFInAppEventParameterName.<< Event Param  >>, <<PLACE_HOLDRER_FOR_PARAM_VALUE>>);
AppsFlyerLib.getInstance().logEvent(getApplicationContext(),
    << Event Name  >>, eventValues);`);

  const [generatedEventCodeSecondary, setGeneratedEventCodeSecondary] =
    useState<string>(`val eventValues = HashMap<String, Any>()
eventValues[<< Event Param  >>] = <<PLACE_HOLDRER_FOR_PARAM_VALUE>>

AppsFlyerLib.getInstance().logEvent(
    applicationContext,
    << Event Name  >> , eventValues)`);

  const isPrefedinedParam = (param: string) => {
    if (isAndroidLike) {
      return androidParams.find((prm) => prm.android_constant_name === param);
    }
    return iosParameter.find((prm) => prm.ios_constant_name === param);
  };

  const reactNativeStep2Code = !isReactNative
    ? `const eventName = '<< Event Name  >>';
      
const eventValues = {
  << Event Param  >>: <<PLACE_HOLDRER_FOR_PARAM_VALUE>>
};`
    : (() => {
          const finalEventName =
            eventName.value?.trim() === ""
              ? `'<< Event Name  >>'`
              : eventName.type === "custom"
                ? `'${eventName.value}'`
                : `'${androidEvents.find((e) => e.android_constant_name === eventName.value)?.event_name || eventName.value}'`;

          const paramLines = eventParams
            .map((param, index) => {
              const eventParam = eventParams[index]?.value;
              const paramName =
                eventParam !== ""
                  ? androidParams.find(
                      (p) => p.android_constant_name === eventParam
                    )?.event_parameter_name || eventParam
                  : "<< Event Param  >>";
              return `  ${paramName}: <<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
            })
            .filter(Boolean);

          return `const eventName = ${finalEventName};
      
const eventValues = {
${paramLines.join(",\n")}
};`;
        })();

  const reactNativeStep3Code = `appsFlyer.logEvent(eventName, eventValues)
  .then((res) => {
    console.log(res);
  })
  .catch((err) => {
    console.error(err);
  }
);`;

  useEffect(() => {
    // Step 3: Sending the event
    if (isAndroid) {
      // Update generatedEventCode based on selectedRadioTwo
      const finalEventName =
        eventName.value?.trim() === ""
          ? eventName.type === "custom"
            ? '"<<Event name>>"'
            : "<<Event name>>"
          : eventName.type === "custom"
            ? `"${eventName.value}"`
            : eventName.value;

      // Use finalEventName like:
      const baseCodeJava = `AppsFlyerLib.getInstance().logEvent(getApplicationContext(),
      ${finalEventName}, eventValues);`;

      const baseCodeKotlin = `AppsFlyerLib.getInstance().logEvent(
      applicationContext,
      ${finalEventName}, eventValues)`;

      const paramLinesJava = eventParams
        .map((param, index) => {
          const eventParam = eventParams[index]?.value;
          return `${
            eventParam !== ""
              ? `eventValues.put(${
                  isPrefedinedParam(eventParam)
                    ? `AFInAppEventParameterName.${eventParam}`
                    : `"${eventParam}"`
                },`
              : "eventValues.put(<< Event Param  >>,"
          } <<PLACE_HOLDRER_FOR_PARAM_VALUE>>);`;
        })
        .filter(Boolean);

      const paramLinesKotlin = eventParams
        .map((param, index) => {
          const eventParam = eventParams[index]?.value;
          return `${
            eventParam !== ""
              ? `eventValues[${
                  isPrefedinedParam(eventParam)
                    ? `AFInAppEventParameterName.${eventParam}] = `
                    : `"${eventParam}" ] =`
                }`
              : "eventValues[<< Event Param  >>] ="
          } <<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
        })
        .filter(Boolean);

      const updatedGeneratedEventCodeJava = `Map<String, Object> eventValues = new HashMap<String, Object>();
${paramLinesJava.join("\n")}
${baseCodeJava}`;

      const updatedGeneratedEventCodeKotlin = `val eventValues = HashMap<String, Any>()
${paramLinesKotlin.join("\n")}
${baseCodeKotlin}`;

      setGeneratedEventCodePrimary(updatedGeneratedEventCodeJava);
      setGeneratedEventCodeSecondary(updatedGeneratedEventCodeKotlin);
    } else if (isReactNative) {
      // Keep the combined code for backward compatibility with other parts of the code
      const updatedGeneratedEventCodeReactNative = `${reactNativeStep2Code}

${reactNativeStep3Code}`;
      setGeneratedEventCodePrimary(updatedGeneratedEventCodeReactNative);
      setGeneratedEventCodeSecondary(updatedGeneratedEventCodeReactNative);
    } else {
      // Update generatedEventCode based on selectedRadioTwo
      // Swift Step 2
      const baseCodeSwift = `AppsFlyerLib.shared().logEvent(name: ${
        (eventName && !(eventName.type === "custom")) ||
        (eventName && eventName.type === "custom")
          ? eventName.type === "custom"
            ? `"${eventName.value ?? "<<Event name>>"}"`
            : (eventName.value ?? "<<Event name>>")
          : "<<Event name>>"
      },`;

      // Objective-C Step 2
      const baseCodeObjC = `[[AppsFlyerLib shared] logEvent:${
        (eventName && !(eventName.type === "custom")) ||
        (eventName && eventName.type === "custom")
          ? eventName.type === "custom"
            ? `"${eventName.value ?? "<<Event name>>"}"`
            : (eventName.value ?? "<<Event name>>")
          : "<<Event name>>"
      }`;

      const paramLinesSwift = eventParams
        .map((param) => {
          const eventParam = param.value;
          return `${
            isPrefedinedParam(eventParam) ? eventParam : `"${eventParam}"`
          }: << Param value >>`;
        })
        .map((line, index, arr) =>
          index < arr.length - 1 ? `  ${line},` : `  ${line}`
        );

      const paramLinesObjC = eventParams
        .map((param) => {
          const eventParam = param.value;
          return `  ${
            isPrefedinedParam(eventParam) ? eventParam : `@"${eventParam}"`
          }: @<< Param value >>`;
        })
        .map((line, index, arr) =>
          index < arr.length - 1 ? line + "," : line
        );

      // Swift
      const updatedGeneratedEventCodeSwift = `${baseCodeSwift}
    values: [
    ${paramLinesSwift.join("\n")}
    ]
  );`;
      // Objective-C
      const updatedGeneratedEventCodeObjC = `${baseCodeObjC}
                                         withValues:@{
                                             ${paramLinesObjC.join("\n")}
                                         }];`;
      setGeneratedEventCodePrimary(updatedGeneratedEventCodeSwift);
      setGeneratedEventCodeSecondary(updatedGeneratedEventCodeObjC);
    }
    // setParamAdded(false);
  }, [eventName, eventName, eventName.type === "custom", eventParams]);

  useEffect(() => {
    const state: ISessionProps = location.state as ISessionProps;
    setProgress(state?.progress);
  }, [location.state]);

  const { os } = location.state || { os: "" };

  const handleClick = () => {
    const progressArr: IProgress[] = progress.slice();
    progressArr[progressArr.length - 1].isListener = responseListenerRequired;

    const progressState: ISessionProps = {
      ...location.state,
      progress: progressArr,
      currentPage: location.state?.currentPage + 1,
    };

    if (tabValue == 1) {
      progressState.isEvgenTesting = false;
      progressState["eventName"] =
        eventName.type === "predefined"
          ? ((isAndroidLike
              ? androidEvents.find(
                  (e) => e.android_constant_name === eventName.value
                )?.event_name
              : iosEvents.find((e) => e.ios_constant_name === eventName.value)
                  ?.event_name) ?? eventName.value)
          : eventName.value;
      progressState["eventParams"] = paramValues
        .filter((x) => x)
        .map((param) => {
          // look for the parameter within the param list and return the event parameter name
          // (unless it's custom and then the original value is returned)
          if (isAndroidLike) {
            return (
              androidParams.find((x) => x.android_constant_name === param)
                ?.event_parameter_name ?? param
            );
          } else {
            return (
              iosParameter.find((x) => x.ios_constant_name === param)
                ?.event_parameter_name ?? param
            );
          }
        });
      amplitudeTrack_continue(location.state, {
        responseListenerRequired,
        eventName: eventName.value,
        eventParams: paramValues,
      });
    } else if (tabValue == 0) {
      progressState.isEvgenTesting = true;
      progressState["evgenEventsObject"] = evgenEventsObject;

      amplitudeTrack_continue(location.state, {
        responseListenerRequired,
        isEvgenTesting: true,
        evgenEventsObject,
      });
    }

    sessionStorage.setItem("previousPage", location.pathname); // Save the current URL before navigation

    navigate(`/inapp/${os.toLowerCase()}/testtype`, {
      state: progressState,
    });
  };

  const codeProps: ICodeBlock = {
    codePrimary: getCodeSnippet("android.imports.basic"),
    codeSecondary: getCodeSnippet("android.kotlin.imports.basic"),
    showLineNumbers: true,
  };
  const codeProps2: ICodeBlock = {
    codePrimary: getCodeSnippet("android.imports.withConstants"),
    codeSecondary: getCodeSnippet("android.kotlin.imports.withConstants"),
    highlight: "2-3",
    highlightSecondary: "2-3",
    showLineNumbers: true,
  };

  const options: IOption[] = isAndroidLike
    ? androidEvents.map((event) => ({
        value: event["android_constant_name"],
        label: event["event_name"],
      }))
    : iosEvents.map((event) => ({
        value: event["ios_constant_name"],
        label: event["event_name"],
      }));

  const getInitialCodeTemplate = (os: OSType) => {
    if (os === OSType.Android) {
      return {
        primary: getCodeSnippet("android.eventCode.java.template"),
        secondary: getCodeSnippet("android.eventCode.kotlin.template"),
      };
    } else if (
      os === OSType.ReactNativeAndroid ||
      os === OSType.ReactNativeIOS
    ) {
      const template = getCodeSnippet("reactNative.eventCode.template");
      return { primary: template, secondary: template };
    }
    return { primary: "", secondary: "" };
  };

  const [inappCodePrimary, setInappCodePrimary] = useState<string>(
    getInitialCodeTemplate(location.state.os).primary
  );
  const [inappCodeSecondary, setInappCodeSecondary] = useState<string>(
    getInitialCodeTemplate(location.state.os).secondary
  );
  const [inappCodeWListenerPrimary, setInappCodeWListenerPrimary] =
    useState<string>("");
  const [inappCodeWListenerSecondary, setInappCodeWListenerSecondary] =
    useState<string>("");

  const [evgenInappCodeJava, setEvgenInappCodePrimary] = useState<string>("");
  const [evgenInappCodeKotlin, setEvgenInappCodeSecondary] =
    useState<string>("");

  const [responseListRequired, setResponseListRequired] =
    useState<string>("No");

  const handleResponseChange = (event) => {
    setResponseListRequired(event.target.value);
    amplitudeTrack_inputChange(
      location.state,
      InputType.Radio,
      "responseListRequired",
      event.target.value
    );

    if (isAndroid) {
      updateEventCodeAndroid();
      updateEventCodeEvgenAndroid(event.target.value);
    } else if (isReactNative) {
      updateEventCodeReactNative();
      updateEventCodeEvgenReactNative(event.target.value);
    } else {
      updateCodeIOS();
      updateEventCodeEvgenIOS(event.target.value);
    }
  };

  const updateEventCodeAndroid = () => {
    const importsWithListener = getCodeSnippet("android.imports.withListener");
    const importsWithoutListener = getCodeSnippet(
      "android.imports.withConstants"
    );
    const successCallback = getCodeSnippet("android.withListener.java.success");
    const errorCallback = getCodeSnippet("android.withListener.java.error");
    const kotlinSuccessCallback = getCodeSnippet(
      "android.withListener.kotlin.success"
    );
    const kotlinErrorCallback = getCodeSnippet(
      "android.withListener.kotlin.error"
    );

    const code1Java = `${importsWithListener}

${inappCodePrimary.replace(
  `eventValues);`,
  `eventValues,
      new AppsFlyerRequestListener() {
${successCallback}
${errorCallback}
      }
  );`
)}`;
    const code2Java = `${importsWithoutListener}

${inappCodePrimary}`;

    const code1Kotlin = `${importsWithListener}

${inappCodeSecondary.replace(
  `eventValues)`,
  `eventValues,
    object : AppsFlyerRequestListener {
${kotlinSuccessCallback}
${kotlinErrorCallback}
    })`
)}`;
    const code2Kotlin = `${importsWithoutListener}

${inappCodeSecondary}`;

    if (responseListRequired == "No") {
      setInappCodeWListenerPrimary(code1Java);
      setInappCodeWListenerSecondary(code1Kotlin);
    } else {
      setInappCodeWListenerPrimary(code2Java);
      setInappCodeWListenerSecondary(code2Kotlin);
    }
  };

  const updateCodeIOS = () => {
    if (generatedEventCodePrimary === "") {
      const swiftTemplate = getCodeSnippet("ios.eventCode.swift.template");
      const objcTemplate = getCodeSnippet("ios.eventCode.objc.template");

      setInappCodeWListenerPrimary(
        interpolateTemplate(swiftTemplate, {
          EVENT_NAME: "<<Event name>>",
          PARAM_LINES: "<<Event Param>>: << Param value >>",
        })
      );
      setInappCodeWListenerSecondary(objcTemplate);
      return;
    }

    const swiftCompletionHandler = getCodeSnippet(
      "ios.withListener.swift.completionHandler"
    );
    const objcCompletionHandler = getCodeSnippet(
      "ios.withListener.objc.completionHandler"
    );

    const code1Swift = `${inappCodePrimary.replace(
      `]
  );`,
      ""
    )}],
${swiftCompletionHandler}`;
    const code2Swift = inappCodePrimary;

    const code1ObjC = `${inappCodeSecondary.replace(
      /withValues:@{|}];/g,
      (match) => {
        if (match === "withValues:@{") {
          return "eventValues:@{";
        }
        if (match === "}];") {
          return "";
        }
        return match; // Fallback for any unforeseen cases
      }
    )}} ${objcCompletionHandler}`;
    const code2ObjC = inappCodeSecondary;

    if (responseListRequired == "No") {
      setInappCodeWListenerPrimary(code1Swift);
      setInappCodeWListenerSecondary(code1ObjC);
    } else {
      setInappCodeWListenerPrimary(code2Swift);
      setInappCodeWListenerSecondary(code2ObjC);
    }
  };

  const updateEventCodeReactNative = () => {
    if (generatedEventCodePrimary === "") {
      const template = getCodeSnippet("reactNative.eventCode.template");
      const defaultCode = interpolateTemplate(template, {
        EVENT_NAME: "'<< Event Name  >>'",
        PARAM_LINES: "  << Event Param  >>: <<PLACE_HOLDRER_FOR_PARAM_VALUE>>",
      });

      setInappCodeWListenerPrimary(defaultCode);
      setInappCodeWListenerSecondary(defaultCode);
      return;
    }

    const code1ReactNative = `${inappCodePrimary.replace(
      `eventValues,
  (err) => {
    console.error(err);
  }
);`,
      ""
    )},
  (err) => {
    console.error(err);
  }
);`;
    const code2ReactNative = inappCodePrimary;

    if (responseListRequired == "No") {
      setInappCodeWListenerPrimary(code1ReactNative);
      setInappCodeWListenerSecondary(code2ReactNative);
    } else {
      setInappCodeWListenerPrimary(code2ReactNative);
      setInappCodeWListenerSecondary(code2ReactNative);
    }
  };

  const updateEventCodeEvgenAndroid = (
    isResponse: string,
    evgenEvents?: EvgenEvent[],
    filteredOptions?: IOption[]
  ) => {
    if (tabValue === 1) {
      return;
    }

    if (!evgenEvents || !evgenEvents.length) {
      evgenEvents = evgenEventsObject.events;
    }
    console.log("evgenEventsObject", evgenEventsObject);
    console.log("evgenEventsObject.events", evgenEventsObject.events);

    // apply filter
    if (filteredOptions) {
      if (filteredOptions.length) {
        const filters = filteredOptions.map((item) => item.value);
        evgenEvents = evgenEvents.filter((event) =>
          filters.includes(event.eventIdentifier)
        );
      }
    } else if (filteredEventOptions.length) {
      const filters = filteredEventOptions.map((item) => item.value);
      evgenEvents = evgenEvents.filter((event) =>
        filters.includes(event.eventIdentifier)
      );
    }

    const evgenInappCodeJava = evgenEvents.map((event) => {
      return `// ${event.eventIdentifier} definition:
Map<String, Object> eventValues = new HashMap<String, Object>();
${event.parameters.map((param) => {
  return `eventValues.put(${
    param.parameterType === "predefined"
      ? `AFInAppEventParameterName.${param.parameterIdentifier.toUpperCase()}`
      : `"${param.parameterIdentifier}"`
  }, <<PLACE_HOLDRER_FOR_PARAM_VALUE>>);`;
}).join(`
`)}
AppsFlyerLib.getInstance().logEvent(getApplicationContext(),
  ${
    event.eventType === "predefined"
      ? `AFInAppEventType.${event.eventIdentifier.toUpperCase()}`
      : `"${event.eventIdentifier}"`
  }, eventValues);`;
    }).join(`

`);

    const evgenInappCodeKotlin = evgenEvents.map((event) => {
      return `// ${event.eventIdentifier} definition:
val eventValues = HashMap<String, Any>()
${event.parameters.map((param) => {
  return `eventValues[${
    param.parameterType === "predefined"
      ? `AFInAppEventParameterName.${param.parameterIdentifier.toUpperCase()}`
      : `"${param.parameterIdentifier}"`
  }] = <<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
}).join(`
`)}
 AppsFlyerLib.getInstance().logEvent(
    applicationContext,
${
  event.eventType === "predefined"
    ? `AFInAppEventType.${event.eventIdentifier.toUpperCase()}`
    : `"${event.eventIdentifier}"`
}, eventValues)`;
    }).join(`

`);

    if (isResponse == "No") {
      setEvgenInappCodePrimary(evgenInappCodeJava);
      setEvgenInappCodeSecondary(evgenInappCodeKotlin);
    } else {
      const evgenInappCodeWListenerJava = evgenInappCodeJava
        .split(`eventValues);`)
        .join(
          `eventValues,
      new AppsFlyerRequestListener() {
        @Override
        public void onSuccess() {
          // YOUR CODE UPON SUCCESS
        }
        @Override
        public void onError(int i, String s) {
          // YOUR CODE FOR ERROR HANDLING
        }
      }
  );`
        );
      setEvgenInappCodePrimary(evgenInappCodeWListenerJava);

      const evgenInappCodeWListenerKotlin = evgenInappCodeJava
        .split(`eventValues)`)
        .join(
          `eventValues,
    object : AppsFlyerRequestListener {
        override fun onSuccess() {
            Log.d(LOG_TAG, "Event sent successfully")
        }
        override fun onError(errorCode: Int, errorDesc: String) {
            Log.d(LOG_TAG, "Event failed to be sent:\n" +
                    "Error code: " + errorCode + "\n"
                    + "Error description: " + errorDesc)
        }
    })`
        );
      setEvgenInappCodeSecondary(evgenInappCodeWListenerKotlin);
    }
  };

  const updateEventCodeEvgenIOS = (
    isResponse: string,
    evgenEvents?: EvgenEvent[],
    filteredOptions?: IOption[]
  ) => {
    if (tabValue === 1) {
      return;
    }

    if (!evgenEvents || !evgenEvents.length) {
      evgenEvents = evgenEventsObject.events;
    }

    // apply filter
    if (filteredOptions) {
      if (filteredOptions.length) {
        const filters = filteredOptions.map((item) => item.value);
        evgenEvents = evgenEvents.filter((event) =>
          filters.includes(event.eventIdentifier)
        );
      }
    } else if (filteredEventOptions.length) {
      const filters = filteredEventOptions.map((item) => item.value);
      evgenEvents = evgenEvents.filter((event) =>
        filters.includes(event.eventIdentifier)
      );
    }

    const evgenInappCodeSwift = evgenEvents.map((event) => {
      return `// ${event.eventIdentifier} definition:
AppsFlyerLib.shared().logEvent(name: ${
        iosEvents.find((evt) => evt.event_name == event.eventIdentifier)
          ?.ios_constant_name ?? `"${event.eventIdentifier}"`
      },
    ${
      event.parameters.length
        ? `values: [
      ${event.parameters.map((param) => {
        return `${
          iosParameter.find(
            (prm) => prm.event_parameter_name == param.parameterIdentifier
          )?.ios_constant_name ?? `"${param.parameterIdentifier}"`
        }: <<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
      }).join(`,
      `)}
    ]`
        : `[
    ]`
    }
  );`;
    }).join(`

`);

    const evgenInappCodeObjC = evgenEvents.map((event) => {
      return `// ${event.eventIdentifier} definition:
[[AppsFlyerLib shared] logEventWithEventName:${
        iosEvents.find((evt) => evt.event_name == event.eventIdentifier)
          ?.ios_constant_name ?? `"${event.eventIdentifier}"`
      }
${
  event.parameters.length
    ? `             withValues:@{
  ${event.parameters.map((param) => {
    return `                     ${
      iosParameter.find(
        (prm) => prm.event_parameter_name == param.parameterIdentifier
      )?.ios_constant_name ?? `"${param.parameterIdentifier}"`
    }: @<<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
  }).join(`
  `)}
                       }`
    : `{}
`
}];`;
    }).join(`

`);
    if (isResponse == "No") {
      setEvgenInappCodePrimary(evgenInappCodeSwift);
      setEvgenInappCodeSecondary(evgenInappCodeObjC);
    } else {
      const evgenInappCodeWListenerSwift = evgenInappCodeSwift
        .split(
          `   ]
  );`
        )
        .join(
          `   ],
    completionHandler: { (response: [String : Any]?, error: Error?) in
      if let response = response {
        print("In app event callback Success: ", response)
      }
      if let error = error {
        print("In app event callback ERROR:", error)
      }
    }
);`
        );
      const evgenInappCodeWListenerObjC = evgenInappCodeObjC
        .split(`withValues:@{`)
        .join(`eventValues:@{`)
        .split(`}];`)
        .join(
          `  completionHandler:^(NSDictionary *response, NSError *error) {
                                   if (response) {
                                       NSLog(@"In app event callback Success: %@", response);
                                   }
                                   if (error) {
                                       NSLog(@"In app event callback ERROR: %@", error);
                                   }
                               }];`
        );
      setEvgenInappCodePrimary(evgenInappCodeWListenerSwift);
      setEvgenInappCodeSecondary(evgenInappCodeWListenerObjC);
    }
  };

  const updateEventCodeEvgenReactNative = (
    isResponse: string,
    evgenEvents?: EvgenEvent[],
    filteredOptions?: IOption[]
  ) => {
    if (tabValue === 1) {
      return;
    }

    if (!evgenEvents || !evgenEvents.length) {
      evgenEvents = evgenEventsObject.events;
    }

    // apply filter
    if (filteredOptions) {
      if (filteredOptions.length) {
        const filters = filteredOptions.map((item) => item.value);
        evgenEvents = evgenEvents.filter((event) =>
          filters.includes(event.eventIdentifier)
        );
      }
    } else if (filteredEventOptions.length) {
      const filters = filteredEventOptions.map((item) => item.value);
      evgenEvents = evgenEvents.filter((event) =>
        filters.includes(event.eventIdentifier)
      );
    }

    const evgenInappCodeReactNative = evgenEvents.map((event) => {
      return `// ${event.eventIdentifier} definition:
const eventName = ${
        androidEvents.find((evt) => evt.event_name == event.eventIdentifier)
          ?.event_name ?? `'${event.eventIdentifier}'`
      };
const eventValues = {
  ${event.parameters.map((param) => {
    return `${
      androidParams.find(
        (prm) => prm.event_parameter_name == param.parameterIdentifier
      )?.event_parameter_name ?? param.parameterIdentifier
    }: <<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
  }).join(`,
  `)}
};

appsFlyer.logEvent(eventName, eventValues)
  .then((res) => {
    console.log(res);
  })
  .catch((err) => {
    console.error(err);
  });`;
    }).join(`

`);

    if (isResponse == "No") {
      setEvgenInappCodePrimary(evgenInappCodeReactNative);
      setEvgenInappCodeSecondary(evgenInappCodeReactNative);
    } else {
      // For React Native, promises already handle success/error, so use the same code
      const evgenInappCodeWListenerReactNative = evgenInappCodeReactNative
        .split(
          `appsFlyer.logEvent(eventName, eventValues)
  .then((res) => {
    console.log(res);
  })
  .catch((err) => {
    console.error(err);
  });`
        )
        .join(
          `appsFlyer.logEvent(eventName, eventValues)
  .then((res) => {
    console.log(res);
  })
  .catch((err) => {
    console.error(err);
  });`
        );
      setEvgenInappCodePrimary(evgenInappCodeWListenerReactNative);
      setEvgenInappCodeSecondary(evgenInappCodeWListenerReactNative);
    }
  };

  useEffect(() => {
    if (generatedEventCodePrimary) {
      setInappCodePrimary(generatedEventCodePrimary);
      setInappCodeSecondary(generatedEventCodeSecondary);
      if (isAndroid) {
        setInappCodeWListenerPrimary(`import com.appsflyer.AppsFlyerLib;
import com.appsflyer.AFInAppEventType; // Predefined event names
import com.appsflyer.AFInAppEventParameterName; // Predefined parameter names

${generatedEventCodePrimary}`);
        setInappCodeWListenerSecondary(`import com.appsflyer.AppsFlyerLib
import com.appsflyer.AFInAppEventType // Predefined event names
import com.appsflyer.AFInAppEventParameterName // Predefined parameter names

  ${setGeneratedEventCodeSecondary}`);
      } else {
        setInappCodeWListenerPrimary(generatedEventCodePrimary);
        setInappCodeWListenerSecondary(generatedEventCodeSecondary);
      }
    }
  }, [generatedEventCodePrimary, generatedEventCodeSecondary]);

  const [evgenEventsObject, setEvgenEventsObject] =
    useState<EvgenEventsObject>();

  const [filteredEventOptions, setFilteredEventOptions] = useState<IOption[]>(
    []
  );

  const filterEvgenEvents = (filteredOptions: IOption[]) => {
    setFilteredEventOptions(filteredOptions);
    if (isAndroid) {
      updateEventCodeEvgenAndroid(
        responseListRequired,
        evgenEventsObject.events,
        filteredOptions
      );
    } else if (isReactNative) {
      updateEventCodeEvgenReactNative(
        responseListRequired,
        evgenEventsObject.events,
        filteredOptions
      );
    } else {
      updateEventCodeEvgenIOS(
        responseListRequired,
        evgenEventsObject.events,
        filteredOptions
      );
    }
  };

  const handleFileUpload = useCallback((newFile, target) => {
    if (newFile) {
      const fileReader = new FileReader();
      fileReader.readAsText(newFile, "UTF-8");
      fileReader.onload = (e) => {
        try {
          const json = JSON.parse(e.target.result.toString());
          if (json?.events) {
            setEvgenEventsObject(json);
            if (isAndroid) {
              updateEventCodeEvgenAndroid("No", json?.events);
            } else if (isReactNative) {
              updateEventCodeEvgenReactNative("No", json?.events);
            } else {
              updateEventCodeEvgenIOS("No", json?.events);
            }
          } else {
            console.error("JSON does not match the EVGEN output structure");
          }
        } catch (error) {
          console.error("JSON parse error: ", error);
        }
      };
    }
  }, []);

  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const stepsAndroid = [
    {
      label: t("ip_AppEvents_android_stepOneHeading"),
      component: (
        <>
          <Typography>
            {t("ip_AppEvents_android_stepOneDescription")}
            <AmplitudeLink
              href={`https://${
                location.state.lang != "en" ? location.state.lang + "." : ""
              }dev.appsflyer.com/hc/docs/android-sdk-reference-appsflyerlib`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <code>{t("ip_AppEvents_android_appsFlyerLibLink")}</code>
            </AmplitudeLink>
          </Typography>

          <DJCodeBlock {...codeProps}></DJCodeBlock>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("ip_AppEvents_android_stepTwoHeading"),
      component: (
        <>
          <Typography>
            {t("ip_AppEvents_android_stepTwoDescription")}
          </Typography>
          <DJCodeBlock {...codeProps2}></DJCodeBlock>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
  ];

  const stepsIOS = [
    {
      label: t("ip_SendInApp_ios_importAppsFlyerLib"),
      component: (
        <>
          <Typography>
            {t("ip_SendInApp_ios_stepOneDescription")}
            <AmplitudeLink
              href={`https://${
                location.state.lang != "en" ? location.state.lang + "." : ""
              }dev.appsflyer.com/hc/docs/ios-sdk-reference-appsflyerlib`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("ip_SendInApp_ios_linkText")}
            </AmplitudeLink>
          </Typography>
          {/* Step 1: Import AppsFlyerLib */}
          <DJCodeBlock
            codePrimary={getCodeSnippet("ios.imports.swift")}
            codeSecondary={getCodeSnippet("ios.imports.objc")}
            showLineNumbers={true}
          />
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
  ];

  const stepsReactNative = [
    {
      label: t("ip_CreateInApp_reactnative_step1_label"),
      component: (
        <>
          <Typography>
            {t("ip_CreateInApp_reactnative_step1_description")}
          </Typography>
          <DJSimpleCodeBlock
            codePrimary='import appsFlyer from "react-native-appsflyer";'
            showLineNumbers={false}
            language="javascript"
          />
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
  ];

  const steps = isAndroid
    ? stepsAndroid
    : isReactNative
      ? stepsReactNative
      : stepsIOS;

  steps.push(
    {
      label: t("ip_SendInApp_android_designInApp"),
      component: (
        <>
          {isReactNative && (
            <Box p={2} bgcolor="warning.light" borderRadius={2} mb={2}>
              <Typography variant="body2" color="warning.dark">
                <Trans
                  i18nKey="ip_CreateInApp_reactnative_step2_warning1"
                  components={{ code: <code />, strong: <strong />, br: <br /> }}
                />
                <br/>
                <Trans i18nKey="ip_CreateInApp_reactnative_step2_warning2" />
                <AmplitudeLink
                  href="https://dev.appsflyer.com/hc/docs/rn_api#logadrevenue"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  {t("ip_CreateInApp_reactnative_step2_warning_link")}
                </AmplitudeLink>
                .
              </Typography>
            </Box>
          )}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="InApp Events Tabs Choose Type"
          >
            <Tab label={t("ip_AppEvents_Evgen_upload")} {...a11yProps(0)} />
            <Tab label={t("ip_AppEvents_Evgen_single")} {...a11yProps(1)} />
          </Tabs>
          <CustomTabPanel value={tabValue} index={0}>
            <Typography variant="h3" sx={{ fontWeight: "400 !important" }}>
              AppsFlyer{" "}
              <AmplitudeLink href="https://evgen.appsflyer.com/">
                In-app event generator
              </AmplitudeLink>
            </Typography>
            <br />
            <DragAndDropUpload
              id="1"
              label={t("ip_AppEvents_Evgen_upload")}
              accept=".json"
              helperText={t("ipAppEvents_Evgen_jsononly")}
              onFileUpload={handleFileUpload}
            />

            {evgenEventsObject?.events?.length ? (
              <Box maxHeight={400} overflow={"auto"}>
                <EventsTable {...evgenEventsObject} />
              </Box>
            ) : null}
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={1}>
            {/* Single InApp */}
            <Box mt={-1}>
              <Typography variant="h3">
                {t("ip_SendInApp_android_stepTwoHeading")}
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2} mt={1}>
                <RadioGroup
                  row
                  value={eventName.type}
                  onChange={handleEventNameTypeChange}
                >
                  <FormControlLabel
                    control={<Radio />}
                    label={t("ip_SendInApp_android_predefinedLabel")}
                    value="predefined"
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label={t("ip_SendInApp_android_customLabel")}
                    value="custom"
                  />
                </RadioGroup>

                {eventName.type === "predefined" ? (
                  <Select
                    id="event-name-select"
                    variant="outlined"
                    size="medium"
                    value={eventName.value}
                    onChange={handleEventNameValueChange}
                    options={options}
                    placeholder={
                      (isAndroidLike
                        ? androidEvents.find(
                            (e) => e.android_constant_name === eventName.value
                          )?.event_name
                        : iosEvents.find(
                            (e) => e.ios_constant_name === eventName.value
                          )?.event_name) || t("ip_SendInApp_ios_chooseOption")
                    }
                  />
                ) : (
                  <TextField
                    variant="outlined"
                    id="event-name-custom"
                    value={eventName.value}
                    onChange={(e) => handleEventNameValueChange(e.target.value)}
                  />
                )}
              </Box>
              <Box>
                <Typography variant="h3">
                  {t("ip_SendInApp_android_stepThreeHeading")}
                </Typography>
                <Box mt={1}>
                  {eventParams.map((param, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      gap={2}
                      onMouseEnter={() => setHoveredRow(index)}
                      onMouseLeave={() => setHoveredRow(null)}
                      mb={2}
                    >
                      <RadioGroup
                        row
                        value={param.type}
                        onChange={(e) => handleParamTypeChange(index, e)}
                      >
                        <FormControlLabel
                          control={<Radio />}
                          label={t("ip_SendInApp_ios_predefined")}
                          value="predefined"
                        />
                        <FormControlLabel
                          control={<Radio />}
                          label={t("ip_SendInApp_ios_custom")}
                          value="custom"
                        />
                      </RadioGroup>

                      {param.type === "predefined" ? (
                        <Select
                          id={`predefined-param-${index}`}
                          variant="outlined"
                          size="medium"
                          placeholder={
                            (isAndroidLike
                              ? androidParams.find(
                                  (p) => p.android_constant_name === param.value
                                )?.event_parameter_name
                              : iosParameter.find(
                                  (p) => p.ios_constant_name === param.value
                                )?.event_parameter_name) ||
                            t("adRevenue_choose_parameter")
                          }
                          value={param.value}
                          onChange={(val) => handleValueChange(index, val)}
                          options={
                            isAndroidLike
                              ? androidParams.map((p) => ({
                                  value: p.android_constant_name,
                                  label: p.event_parameter_name,
                                }))
                              : iosParameter.map((p) => ({
                                  value: p.ios_constant_name,
                                  label: p.event_parameter_name,
                                }))
                          }
                        />
                      ) : (
                        <TextField
                          variant="outlined"
                          id={`custom-param-${index}`}
                          placeholder={t("adRevenue_custom_parameter")}
                          value={param.value}
                          onChange={(e) =>
                            handleParamValueChange(index, e.target.value)
                          }
                        />
                      )}

                      {hoveredRow === index && (
                        <IconButton
                          onClick={() => handleRemoveParameter(index)}
                        >
                          <DeleteOutlineOutlinedIcon color="error" />
                        </IconButton>
                      )}
                    </Box>
                  ))}

                  {eventParams.length < 5 && (
                    <Button
                      onClick={handleAddParameter}
                      variant="outlined"
                      size="large"
                      sx={{ borderRadius: "100px" }}
                      startIcon={<AddRoundedIcon />}
                    >
                      {t("adRevenue_add_parameter")}
                    </Button>
                  )}
                </Box>
              </Box>
              <Box>
                <br />
                <Typography variant="h2">
                  {t("ip_SendInApp_android_generatedEventCodeHeading")}
                </Typography>

                {/* Step 2: Generated event code */}
                {isReactNative ? (
                  <Box mt={1}>
                  <DJSimpleCodeBlock
                    codePrimary={reactNativeStep2Code}
                      showLineNumbers={true}
                      wrapLongLines={false}
                      language={"javascript"}
                    />
                  </Box>
                ) : (
                  <DJCodeBlock
                    codePrimary={generatedEventCodePrimary}
                    codeSecondary={generatedEventCodeSecondary}
                    showLineNumbers={true}
                    wrapLongLines={false}
                  />
                )}
              </Box>
            </Box>
          </CustomTabPanel>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("ip_AppEventInApp_android_stepFiveHeading"),
      component: (
        <>
          <Typography>
            {t("ip_AppEventInApp_android_logEventDescription")}
          </Typography>{" "}
          {!isReactNative && (
              <Box mt={2}>
                <Typography variant="body1">
                  {t("ip_AppEventInApp_android_responseListenerLabel")}
                  <ExplanationTooltip />
                </Typography>
                <RadioGroup
                  row
                  value={responseListRequired}
                  onChange={handleResponseChange}
                >
                  <FormControlLabel
                    label={t("ip_AppEventInApp_android_noLabel")}
                    control={<Radio checked={!responseListenerRequired} />}
                    onChange={() => setResponseListenerRequired(false)}
                    value="No"
                  />
                  <FormControlLabel
                    label={t("ip_AppEventInApp_android_yesLabel")}
                    control={<Radio checked={responseListenerRequired} />}
                    onChange={() => setResponseListenerRequired(true)}
                    value="Yes"
                  />
                </RadioGroup>
              </Box>
            )}
            {isReactNative ? (
              <>
                {evgenEventsObject?.events?.length && (
                  <MultiSelectControl
                    options={evgenEventsObject.events.map((event) => {
                      return {
                        value: event.eventIdentifier,
                        label: event.eventName,
                      };
                    })}
                    onSelectedEventsChange={(selectedValues) =>
                      filterEvgenEvents(selectedValues)
                    }
                  ></MultiSelectControl>
                )}
                <Box mt={2}>
                  {tabValue == 1 ? (
                    <DJSimpleCodeBlock
                      codePrimary={reactNativeStep3Code}
                      showLineNumbers={true}
                      wrapLongLines={false}
                      language="javascript"
                    />
                  ) : (
                    <DJSimpleCodeBlock
                      codePrimary={evgenInappCodeJava}
                      showLineNumbers={true}
                      wrapLongLines={false}
                      language="javascript"
                    />
                  )}
                </Box>
              </>
            ) : (
                <>
          {tabValue == 1 && (
            <DJCodeBlock
              codePrimary={inappCodeWListenerPrimary}
              codeSecondary={inappCodeWListenerSecondary}
              showLineNumbers={true}
            />
          )}
          {tabValue == 0 && (
            <>
              {evgenEventsObject?.events?.length && (
                <MultiSelectControl
                  options={evgenEventsObject.events.map((event) => {
                    return {
                      value: event.eventIdentifier,
                      label: event.eventName,
                    };
                  })}
                  onSelectedEventsChange={(selectedValues) =>
                    filterEvgenEvents(selectedValues)
                  }
                ></MultiSelectControl>
              )}
                <DJCodeBlock
                  codePrimary={evgenInappCodeJava}
                  codeSecondary={evgenInappCodeKotlin}
                  showLineNumbers={true}
                  wrapLongLines={false}
                />
            </>
          )}
          </>
          )}
        </>
      ),
      shouldDisplayNavigationButtons: true,
    }
  );

  return (
    <Container maxWidth={"lg"}>
      <Box padding={3} style={{ minHeight: "50vh" }}>
        <Typography sx={{ mb: 2 }} variant="h1">
          {t("ip_SendInApp_android_sendInAppEventHeading")}
        </Typography>
        <Stepper
          activeStep={activeStep}
          style={{ border: "none" }}
          orientation="vertical"
        >
          {steps.map(({ label, component, shouldDisplayNavigationButtons }) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {component}
                {shouldDisplayNavigationButtons && (
                  <Box sx={{ mt: 2 }}>
                    {activeStep !== 0 && (
                      <Button
                        variant="text"
                        color="primary"
                        onClick={handleBack}
                      >
                        {t("general_Back")}
                      </Button>
                    )}
                    {activeStep !== steps.length - 1 && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        disabled={
                          (isAndroid &&
                            activeStep === 2 &&
                            ((tabValue == 1 &&
                              ((!(eventName.type === "custom") && !eventName) ||
                                (eventName.type === "custom" && !eventName) ||
                                eventParams.length === 0 ||
                                !eventParams[0]?.value)) ||
                              (tabValue == 0 && !evgenEventsObject))) ||
                          (isIOS &&
                            activeStep === 1 &&
                            ((tabValue == 1 &&
                              ((!(eventName.type === "custom") && !eventName) ||
                                (eventName.type === "custom" && !eventName) ||
                                eventParams.length === 0 ||
                                !eventParams[0]?.value)) ||
                              (tabValue == 0 && !evgenEventsObject))) ||
                          (isReactNative &&
                            activeStep === 1 &&
                            ((tabValue == 1 &&
                              ((!(eventName.type === "custom") && !eventName) ||
                                (eventName.type === "custom" && !eventName) ||
                                eventParams.length === 0 ||
                                !eventParams[0]?.value)) ||
                              (tabValue == 0 && !evgenEventsObject)))
                        }
                      >
                        {activeStep === steps.length - 1
                          ? t("general_Finish")
                          : t("general_Next")}
                      </Button>
                    )}
                  </Box>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box
        display={"flex"}
        width={"100%"}
        justifyContent="space-between"
        alignSelf="end"
        justifySelf="end"
        marginTop={10}
        textAlign="end"
      >
        {" "}
        <Button
          variant="outlined"
          size="medium"
          color="secondary"
          style={{ marginRight: "5px" }}
          onClick={() => navigate(-1)}
        >
          {t("general_Back")}
        </Button>
        <Button
          variant="contained"
          size="medium"
          color="primary"
          disabled={activeStep != steps.length - 1}
          onClick={() => handleClick()}
        >
          {t("general_Continue")}
        </Button>
      </Box>
    </Container>
  );
};

export default transition(SendInapp);
